import { computed } from 'vue';
import { CampaignType } from '@studyportals/campaign-management-api-interface';
import { BudgetTypes } from '@/models/budget.interface';
import { IInputStringEvent } from '@/models/input-events.interface';
import { ValidationProperties } from '@/models/validation.interface';
import validationPresenter from '@/presenters/validation-presenter';
import customBudgetPresenter from '@/presenters/custom-budget-presenter';
import store from '@/store';

export default class BudgetCode {

	public get capitalisedType(): string {
		return this.type.charAt(0).toUpperCase() + this.type.slice(1);
	}

	public storedBudget = computed((): number | null => {
		if (this.type === BudgetTypes.LISTING) {
			return store.getters.budgetListing();
		} else {
			return store.getters.budgetCustom();
		}
	});

	public isFieldDisabled = computed((): boolean => {
		const campaignType = store.getters.campaignType();
		return campaignType === CampaignType.CUSTOM_PRODUCTS && this.type === BudgetTypes.LISTING;
	});

	public validationMessage = computed((): string => {
		if (this.isFieldValidatedAndInvalid.value) {
			return 'This field must be a valid number';
		}

		return 'This field must be completed before proceeding';
	});

	public isFieldValidatedAndMissing = computed((): boolean => {
		return this.type === BudgetTypes.LISTING ?
			validationPresenter.isPropertyMissing(ValidationProperties.LISTING_BUDGET) :
			validationPresenter.isPropertyMissing(ValidationProperties.CUSTOM_PRODUCTS_BUDGET);
	});

	public isFieldValidatedAndInvalid = computed((): boolean => {
		return this.type === BudgetTypes.LISTING ?
			validationPresenter.isPropertyInvalid(ValidationProperties.LISTING_BUDGET) :
			validationPresenter.isPropertyInvalid(ValidationProperties.CUSTOM_PRODUCTS_BUDGET);
	});

	public isBudgetTypeCustom = computed((): boolean => {
		return this.type === BudgetTypes.CUSTOM;
	});

	public budgetLeft = computed((): string => {
		const budget = customBudgetPresenter.budgetLeftFormatted.value;
		const budgetValueAsString = this.isBudgetLeftNegative.value ? `- ${budget}` : budget.toString();
		return `${budgetValueAsString} ${this.currency.value}`;
	});

	public budgetTotal = computed((): string => {
		const budget = customBudgetPresenter.budgetTotalFormatted.value;
		return `${budget} ${this.currency.value}`;
	});

	private isBudgetLeftNegative = computed((): boolean => {
		return customBudgetPresenter.isBudgetLeftNegative.value;
	});

	private currency = computed((): string => {
		const storedValue = store.getters.currency();
		return storedValue ? storedValue : '';
	});

	constructor(
		private type: BudgetTypes
	) { }

	public setBudgetBasedOnEvent(event: IInputStringEvent): void {
		const value = event?.target?.value;
		if (value === undefined) {
			return;
		}

		if (this.type === BudgetTypes.LISTING) {
			store.mutations.setBudgetListing(parseInt(value));
		} else {
			store.mutations.setBudgetCustom(parseInt(value));
		}

		if (!this.isFieldValidatedAndMissing.value && !this.isFieldValidatedAndInvalid.value) {
			return;
		}

		validationPresenter.validateMandatoryFields();
	}
}
